import React from 'react';
import styled from '@emotion/styled';
import { Plus, Minus, ShoppingBag } from 'lucide-react';
import { useModal } from 'context/ModalContext';
import { useContent } from 'context/ContentContext';
import { useCart } from 'context/CartContext';
import {
  createUniqueItemId,
  formatModifiersForCart,
} from 'utils/menuItemUtils';

const FooterContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-top: 2px solid ${({ theme }) => theme.colors.offWhiteDark};
  padding: 1.6rem 2rem;
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    padding: 1.2rem 1.6rem env(safe-area-inset-bottom);
    gap: 1.2rem;
    padding-bottom: calc(1.2rem + env(safe-area-inset-bottom));
  }
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const QuantityButton = styled.button`
  width: 52px;
  height: 52px;
  border: 1.5px solid ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => `${theme.colors.primary}14`};
  border-radius: 9999px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  color: ${({ theme }) => theme.colors.primary};

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    border-color: transparent;
    color: ${({ theme }) => theme.colors.white};
    transform: translateY(-2px);
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const QuantityDisplay = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.black};
  min-width: 48px;
  text-align: center;
`;

const AddToCartButton = styled.button`
  flex: 1;
  height: 56px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: 14px;
  font-size: 1.6rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  transition: all 0.2s;
  box-shadow: ${({ theme }) =>
    `0 6px 20px ${theme.colors.primary}30, inset 0 1px 0 rgba(255,255,255,0.12)`};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => `0 8px 24px ${theme.colors.primary}40`};
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
    gap: 0.6rem;
  }
`;

const ButtonText = styled.span`
  @media (max-width: 768px) {
    display: none;
  }
`;

const ButtonPrice = styled.span`
  font-size: 1.8rem;
  font-weight: 800;
  margin-left: 0.5rem;
  opacity: 0.9;

  @media (max-width: 768px) {
    margin-left: 0;
    font-size: 1.6rem;
  }
`;

const formatPrice = (price) => {
  if (typeof price === 'string') return price;
  return `$${(price / 100).toFixed(2)}`;
};

export const ModalFooter = () => {
  const {
    item,
    currentQuantity,
    selectedModifiers,
    incrementQuantity,
    decrementQuantity,
    calculateTotalPrice,
    isValidSelection,
    existingInCart,
    existingCartItemId,
    closeModal,
  } = useModal();

  const { messages } = useContent();
  const { addToCart, updateCartItem } = useCart();

  const handleAddToCart = () => {
    if (!isValidSelection()) return;

    // Use the simple base ID without encoding modifiers
    const uniqueId = createUniqueItemId(item.id);

    // Format modifiers for cart display
    const formattedModifiers = formatModifiersForCart(
      selectedModifiers,
      item.modifierGroups,
    );

    const cartItem = {
      ...item,
      id: uniqueId,
      // Include both formats for backward compatibility
      modifiers: formattedModifiers, // Formatted for display
      selectedModifiers: selectedModifiers, // Original selection object (group -> modifier mapping)
      quantity: currentQuantity,
      displayPrice: formatPrice(calculateTotalPrice() / currentQuantity),
      price: calculateTotalPrice() / currentQuantity,
      originalItem: { ...item },
    };

    if (existingInCart) {
      // Replace the row that was re-opened, not a lookalike: the cart may hold
      // the same item twice with different modifiers.
      updateCartItem(existingCartItemId, cartItem);
    } else {
      addToCart(cartItem);
    }

    closeModal();
  };

  const totalPrice = formatPrice(calculateTotalPrice());
  const isValid = isValidSelection();
  const actionLabel = existingInCart
    ? messages?.modal?.updateCart || 'Update Cart'
    : isValid
      ? messages?.modal?.addToCart || 'Add to Cart'
      : messages?.modal?.selectOptions || 'Select Options';

  return (
    <FooterContainer>
      <QuantityControls>
        <QuantityButton
          onClick={decrementQuantity}
          disabled={currentQuantity <= 1}
          aria-label="Decrease quantity"
        >
          <Minus size={18} strokeWidth={3} />
        </QuantityButton>
        <QuantityDisplay>{currentQuantity}</QuantityDisplay>
        <QuantityButton
          onClick={incrementQuantity}
          aria-label="Increase quantity"
        >
          <Plus size={18} strokeWidth={3} />
        </QuantityButton>
      </QuantityControls>

      <AddToCartButton
        onClick={handleAddToCart}
        disabled={!isValid}
        // Under 768px the text span is hidden and only the bag icon and price
        // remain visible, which left the accessible name as "$9.99" (#245).
        aria-label={`${actionLabel}, ${totalPrice}`}
        title={
          !isValid
            ? messages?.modal?.pleaseSelectRequired ||
              'Please select all required options'
            : ''
        }
      >
        <ShoppingBag size={20} strokeWidth={2.5} />
        <ButtonText>{actionLabel}</ButtonText>
        <ButtonPrice>{totalPrice}</ButtonPrice>
      </AddToCartButton>
    </FooterContainer>
  );
};

export default ModalFooter;
